import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  canCreateProjectBoard,
  canUpdateProjectBoard,
  canDeleteProjectBoard,
} from "../../../app/utils/projectBoard/projectBoardPermissions";
import {
  fetchAllBoards,
  deleteBoardApi,
} from "../../../app/utils/projectBoard/projectBoardApi";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import Loader from "../../atoms/Loader/Loader";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import {
  BoardButton,
  BoardEmptyState,
  BoardSearchInput,
} from "../../atoms/BoardDesignSystem";
import BoardTile from "../../molecules/BoardTile/BoardTile";
import BoardCreateModal from "../../molecules/BoardCreateModal/BoardCreateModal";
import "./ProjectBoardDashboardPage.scss";

const ProjectBoardDashboardPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardToDelete, setBoardToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canCreate = canCreateProjectBoard();
  const canUpdate = canUpdateProjectBoard();
  const canDelete = canDeleteProjectBoard();

  const loadBoards = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllBoards(searchQuery);
      setBoards(data);
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.message || "Failed to load boards.";
      showErrorToast(msg);
      setBoards([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(loadBoards, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadBoards, searchQuery]);

  const handleCreated = (boardId) => {
    if (boardId) {
      navigate(`/project-management/${boardId}`);
    } else {
      loadBoards();
    }
  };

  const handleDeleteBoard = async () => {
    const boardId = boardToDelete?.boardId || boardToDelete?.board_id;
    if (!boardId || deleting) return;
    setDeleting(true);
    try {
      await deleteBoardApi(boardId);
      showSuccessToast("Board deleted.");
      setBoardToDelete(null);
      setBoards((prev) => prev.filter((b) => (b.boardId || b.board_id) !== boardId));
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to delete board.";
      showErrorToast(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="pb-page project-board-dashboard shell-content-wrap">
      <header className="pb-page__hero">
        <div className="pb-page__hero-main">
          <h1 className="page-title">Project Management</h1>
          <p className="page-subtitle">
            Manage tasks on TCS|ST boards linked to your projects.
          </p>
        </div>
        {canCreate && (
          <div className="pb-page__hero-actions">
            <BoardButton variant="primary" onClick={() => setCreateOpen(true)}>
              Create Board
            </BoardButton>
          </div>
        )}
      </header>

      <div className="pb-toolbar project-board-dashboard__toolbar">
        <BoardSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search boards..."
        />
      </div>

      {loading ? (
        <div className="project-board-dashboard__loading">
          <Loader />
        </div>
      ) : boards.length > 0 ? (
        <div className="pb-board-grid">
          {boards.map((board) => (
            <BoardTile
              key={board.boardId || board.board_id || board.name}
              board={board}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onEdit={setEditingBoard}
              onDelete={setBoardToDelete}
            />
          ))}
        </div>
      ) : (
        <div className="card-border card-body">
          <BoardEmptyState
            icon={searchQuery ? "🔍" : "📋"}
            title={searchQuery ? "No boards found" : "No boards yet"}
            description={
              searchQuery
                ? "Try a different search term."
                : canCreate
                  ? "Create a board linked to one of your projects to start tracking tasks."
                  : "No boards are available yet. Contact your administrator to create one."
            }
            actionLabel={!searchQuery && canCreate ? "Create Board" : undefined}
            onAction={!searchQuery && canCreate ? () => setCreateOpen(true) : undefined}
          />
        </div>
      )}

      <BoardCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <BoardCreateModal
        open={Boolean(editingBoard)}
        board={editingBoard}
        onClose={() => setEditingBoard(null)}
        onUpdated={() => {
          setEditingBoard(null);
          loadBoards();
        }}
      />

      <ConfirmModal
        isOpen={Boolean(boardToDelete)}
        onClose={() => !deleting && setBoardToDelete(null)}
        onConfirm={handleDeleteBoard}
        title="Delete board?"
        content={`Delete "${boardToDelete?.name || "this board"}"? Lists and cards on this board will no longer be available.`}
        confirmText="Delete board"
        confirmLoading={deleting}
      />
    </div>
  );
};

export default ProjectBoardDashboardPage;
